import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
// State
import { UserContextState } from '../auth/user-context.state';
import {
	Logout,
	SetLoginMessage,
	SetIsHasPermissions,
} from '../auth/action/auth.action';
import { SetCurrentModule, SetCurrentUrl } from '../action/state.action';
// Model
import {
	AUTHROLE,
	AUTHPATH,
	AUTHACTIONS,
	AUTHREDIRECTIONACTION,
} from '../model/role.model';
import { UserContextModel } from '../auth/user-context.model';
// Service
import { ApiService } from './api.service';
import { AppService } from './app.service';
import { SignalRService } from '../signal-r/service/signal-r.service';
import { AppInformationService } from './app-info.service';
import { GlobalUtils } from '../utils/global.utils';

@Injectable({providedIn: 'root'})
export class RoleService {
	constructor(
		private router: Router,
		private oidcSecurityService: OidcSecurityService,
		private store: Store,
		private signalRService: SignalRService,
		private apiService: ApiService,
		private appService: AppService,
		private appInfo: AppInformationService
	) {
	}

	/**
	 * set the default route depend on the permission
	 */
	setPathByRole(state: RouterStateSnapshot): boolean {
		const userContextModel: UserContextModel = this.store.selectSnapshot(UserContextState.getUserContext);

		const permissions = (userContextModel.permission || [])
			.filter((r: any) => r !== AUTHROLE.NOTALLOWED);

		const currentUrl = this.appInfo.currentUrl;
		let url = currentUrl;
		if (!currentUrl) {
			url = state.url;
		}

		// because I don't trust backend anymore * Agree, confirmed again 2 years later.
		if (permissions.length) {
			const role = this.appService.getRoleByPermission(permissions);

			if (role.return) {
				this.setRedirection(url, role.role);
				return true;
			} else {
				this.unAuthSetValues(AUTHACTIONS.UNAUTHORIZED);
				return null;
			}
		} else if (
			userContextModel &&
			(userContextModel.permission && userContextModel.permission.length === 0) ||
			(userContextModel.role && userContextModel.role.length === 0)
		) {
			this.unAuthSetValues(AUTHACTIONS.PERMISSION_ROLES_REQUIRED);
			return null;
		} else {
			this.unAuthSetValues(AUTHACTIONS.UNAUTHORIZED);
			return null;
		}
	}

	/**
	 * validate user role
	 */
	checkUserRole(state: RouterStateSnapshot, forceKeepUrl = false): boolean {
		const permissions = (this.store.selectSnapshot(UserContextState.permission) || [])
			.filter((r: any) => r !== AUTHROLE.NOTALLOWED);

		if (permissions.length) {
			const currentUrl = this.appInfo.currentUrl;
			let urlTriedToVisit = state.url;
			let url = currentUrl;
			if (!currentUrl) {
				url = urlTriedToVisit;
			}

			if (forceKeepUrl) {
				urlTriedToVisit = url;
			}

			this.store.dispatch(new SetCurrentUrl(urlTriedToVisit, this.appInfo.appName));
			const role = this.appService.getRoleByPermissionAndUrl(permissions, url);
			if (role.return) {
				this.setRedirection(url, role.role);

				return true;
			} else {
				// console.warn('This user does not have the ROLE');
				this.apiService.setSystemApp(role.path);

				if (role.role === '') {
					// If user is trying to access a not allowed path, go to default path
					const defaultUserPath = this.goToDefaultUserPath(permissions);
					// If nothing was found, kick the user completely out of the app
					if (!defaultUserPath) {
						this.unAuthSetValues(AUTHACTIONS.PERMISSION_ROLES_REQUIRED);
					}
				} else {
					return true;
				}
			}
			return null;
		} else {
			this.unAuthSetValues(AUTHACTIONS.UNAUTHORIZED);
			return null;
		}
	}

	/**
	 * Redirect user to default PATH
	 *
	 * @param permission any
	 */
	private goToDefaultUserPath(permission: any): any {
		const path = this.appService.getDefaultPathByPermissions(permission);

		if (path !== '') {
			this.router.navigate([`${path}`]).catch((e) => console.warn(e));
		}

		return null;
	}

	/**
	 * set internal url
	 *
	 * @param url
	 * @param role
	 */
	private setRedirection(url: string, role: string): void {
		const redirData = this.appService.getRedirectionData(url, role);
		if (redirData.isAdmin) {
			this.store.dispatch(
				new SetCurrentModule(redirData.path, this.appInfo.appName)
			);
		}

		this.apiService.setSystemApp(redirData.path);
		this.router.navigate([`${redirData.path}`]).catch((e) => console.warn(e));
	}

	/**
	 * Set unautharize route and values and redirect the user outside the app
	 * @param action
	 * @param errorPage
	 */
	unAuthSetValues(action: AUTHACTIONS): void {
		AUTHREDIRECTIONACTION.forEach((actions) => {
			if (actions.TYPE === action) {
				if (!GlobalUtils.isInsideIframe()) {
					this.store.dispatch(new SetCurrentUrl(null, this.appInfo.appName));
					this.store.dispatch(new SetCurrentModule(null, this.appInfo.appName));
				}

				this.store.dispatch(new Logout(actions.FORCE));
				this.store.dispatch(new SetLoginMessage(actions.MSG));

				if (
					actions.TYPE === AUTHACTIONS.UNAUTHORIZED ||
					actions.TYPE === AUTHACTIONS.LOGOUT ||
					actions.TYPE === AUTHACTIONS.PERMISSION_ROLES_REQUIRED
				) {
					this.oidcSecurityService.logoff();
				} else {
					this.router
						.navigate([`/${AUTHPATH.UNAUTHORIZED}`])
						.catch((e) => console.warn(e));
				}
			}
		});
	}

	/**
	 * call this when users does not have access to specific area or orders
	 *
	 * @param url
	 */
	notFoundAction(url: string, currentModule: string, code: number): void {
		const message = code === 404 ? 'Not found' : 'Access denied';
		this.store.dispatch(
			new SetIsHasPermissions({code: code, message: message})
		);

		const appModule = this.appService.getDefaultPath(url, currentModule);
		if (appModule && appModule.redirect) {
			this.router.navigate([`${appModule.path}`]).catch((e) => console.warn(e));
		}
	}

	/**
	 * set signalR by Role
	 */
	setSignalRbyRole(module: string): void {
		this.signalRService.startConnection(module);
		this.signalRService.registerChannel('orders');
	}

	/**
	 * Given the current route & the user role, skip the auth.guard.
	 *
	 * @param currentRoute
	 * @param role
	 * @returns boolean
	 */
	skipCanActivateGuard(currentRoute: any, role: any): boolean {
		return this.appService.getSkipCanActivateGuard(currentRoute, role);
	}
}
